import React from "react";
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../../../contexts/appContext";
import {
  ROUTES_ADMIN,
  ROUTES_CERTIFIER_BODY_ADMIN,
  ROUTES_CERTIFIER,
  ROUTES_FACTORY,
  ROUTES_WHOLESALER,
  ROUTES_USER,
  ROUTES_STORE,
  ROUTES_SUPER_ADMIN
} from "../../../router/route_list.js";
import {
  Box,
  Avatar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";

const roleRoutes = {
  Admin: ROUTES_ADMIN,
  Certifier_Body_Admin: ROUTES_CERTIFIER_BODY_ADMIN,
  Certifier: ROUTES_CERTIFIER,
  Factory: ROUTES_FACTORY,
  Wholesaler: ROUTES_WHOLESALER,
  user: ROUTES_USER,
  Store: ROUTES_STORE,
  Super_Admin : ROUTES_SUPER_ADMIN,
  
};

const Sidebar = ({ 
  onMenuClick,
  width = 260,
  showUserInfo = true 
}) => {
  const { user, loadingUser } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  if (loadingUser) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Paper elevation={3} sx={{ p: 3, m: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Veuillez vous connecter pour voir la barre latérale.
        </Typography>
      </Paper>
    );
  }

  const routes = roleRoutes[user.role] || [];

  const handleNavigation = (path) => {
    navigate(path);
    if (onMenuClick) {
      onMenuClick();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: isSmallScreen ? "relative" : "fixed",
        left: 0,
        top: isSmallScreen ? 0 : 64,
        bottom: 0,
        zIndex: 1000,
        p: 2,
        boxShadow: "none",
        overflow: "auto",
        width: isSmallScreen ? "100%" : width,
        display: "flex",
        flexDirection: "column",
        bgcolor: "primary.dark",
        color: "primary.contrastText",
        scrollbarWidth: "thin",
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'primary.light',
          borderRadius: 3,
        }
      }}
    >
      {/* User Info Section */}
      {showUserInfo && (
        <>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            mb={3}
            pt={1}
          >
            <Avatar
              src={user.photoURL || "https://via.placeholder.com/40"}
              alt="Profile"
              sx={{
                width: 64,
                height: 64,
                mb: 1.5,
                border: `2px solid ${theme.palette.primary.contrastText}`,
              }}
            />
            <Typography variant="subtitle1" fontWeight="medium" textAlign="center" color="grey.100">
              {user.displayName || user.user_name || 'Utilisateur'}
            </Typography>
            <Typography variant="body2" textAlign="center" sx={{ opacity: 0.8, mt: 0.5 }}>
              {user.email || user.user_email}
            </Typography>
            <Box
              sx={{
                mt: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'primary.light',
              }}
            >
              <Typography variant="caption" fontWeight="medium">
                {user.role ? user.role.replace(/_/g, ' ') : 'Utilisateur'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ bgcolor: "primary.contrastText", opacity: 0.2, mb: 2 }} />
        </>
      )}

      {/* Navigation Links */}
      <List dense sx={{ flexGrow: 1 }}>
        {routes.map(({ path, label }) => {
          const isActive = location.pathname === path;
          return (
            label && (
              <ListItem key={path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(path)}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    color: "primary.contrastText",
                    bgcolor: isActive ? "primary.light" : "transparent",
                    "&:hover": {
                      bgcolor: "primary.main",
                      transform: "translateX(4px)",
                      transition: "all 0.2s ease",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: isActive ? "medium" : "regular",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )
          );
        })}
      </List>
    </Paper>
  );
};

Sidebar.propTypes = {
  onMenuClick: PropTypes.func,
  width: PropTypes.number,
  showUserInfo: PropTypes.bool
};

export default Sidebar;